// Fragment "gateway wallet" — Payment Gateway v1, GatewayWallet.jsx + WithdrawalForm.jsx.
// Replaces the old "stx.payout*" strip (payout redesign SHIM) with the real four-bucket
// wallet. Payout row states render through "gw.state.*" per the contract's §7 convention
// (shared spelling with other enums is intentional — "rejected" means the same thing
// whether it is a payout or a pricing proposal).
export default {
  fr: {
    "gw.wallet.title": "Portefeuille",
    "gw.wallet.loading": "Chargement du solde…",
    "gw.wallet.loadError": "Impossible de charger le solde pour le moment.",
    "gw.wallet.retry": "Réessayer",

    "gw.wallet.frozenBanner": "Vos reversements sont gelés. Contactez Solunary pour en connaître la raison.",
    "gw.wallet.debtBanner": "Vous devez {amount} à Solunary — ce montant sera déduit de vos prochains reversements.",
    "gw.wallet.commission": "Commission Solunary sur les ventes encaissées par la passerelle : {pct} %.",
    "gw.wallet.adjustments": "Ajustements cumulés : {amount}",

    "gw.wallet.bucket.pending": "En cours d'encaissement",
    "gw.wallet.bucket.available": "Disponible",
    "gw.wallet.bucket.reserved": "Réservé",
    "gw.wallet.bucket.lifetimePaid": "Total déjà reversé",

    "gw.wallet.holdsTitle": "Échéancier de libération",
    "gw.wallet.holdReleasesAt": "Libéré le {date}",

    "gw.wallet.withdrawAmountLabel": "Montant à retirer",
    "gw.wallet.requestWithdrawal": "Demander un retrait",
    "gw.wallet.frozenBlocksWithdrawal": "Les retraits sont bloqués tant que votre compte est gelé.",
    "gw.wallet.destinationNotReady": "Aucun retrait n'est possible tant que votre compte bancaire de versement n'est pas connecté.",
    "gw.wallet.destinationSetupCta": "Configurer mon compte de versement",
    "gw.wallet.belowMinimum": "Le retrait minimum est de {min}.",
    "gw.wallet.aboveAvailable": "Vous ne pouvez pas retirer plus que le disponible ({available}).",
    "gw.wallet.expectsReview": "Les demandes supérieures à {max} sont examinées par une personne — un délai est à prévoir.",

    "gw.wallet.confirmTitle": "Confirmer le retrait",
    "gw.wallet.confirmDesc": "Ce montant sera envoyé dès l'approbation par Solunary.",
    "gw.wallet.receiveNow": "Vous recevez {amount} maintenant.",
    "gw.wallet.reserveHeld": "{amount} ({pct} %) sont retenus en réserve glissante jusqu'au {date}.",
    "gw.wallet.crossBorderNote": "Votre compte bancaire est à l'étranger : Stripe prélève des frais de transfert international, déduits de votre solde.",
    "gw.wallet.conversionEstimate": "Stripe convertit ce versement en {currency}. Estimation au taux auquel vos ventes récentes ont été réglées : {gross}, moins des frais de conversion d'environ {fee} ({pct} %), soit environ {net} reçus. Le taux réel est fixé par Stripe au moment du versement.",
    "gw.wallet.conversionNoHistory": "Stripe convertit ce versement en {currency} et prélève des frais de conversion d'environ {pct} %. Aucune estimation en {currency} n'est possible tant qu'aucune de vos ventes en {currency} n'a été réglée.",
    "gw.wallet.cancel": "Annuler",
    "gw.wallet.confirmSend": "Confirmer",
    "gw.wallet.withdrawalApproved": "Retrait approuvé automatiquement.",
    "gw.wallet.withdrawalRequested": "Demande de retrait envoyée.",

    "gw.wallet.historyTitle": "Historique des retraits",
    "gw.wallet.historyEmpty": "Aucun retrait pour le moment.",
    "gw.wallet.colAmount": "Montant",
    "gw.wallet.colStatus": "Statut",
    "gw.wallet.colRequested": "Demandé le",
    "gw.wallet.colFailure": "Motif d'échec",
    "gw.wallet.bankLegFailedHint": "Les fonds restent sur votre solde Stripe. Mettez à jour vos coordonnées bancaires dans Stripe pour les recevoir.",

    "gw.state.reserving": "Réservation en cours",
    "gw.state.requested": "Demandé",
    "gw.state.approved": "Approuvé",
    "gw.state.transfer_created": "Virement initié",
    "gw.state.transfer_unknown": "Vérification auprès de la banque",
    "gw.state.payout_pending": "Virement en cours",
    "gw.state.paid": "Reversé",
    "gw.state.payout_leg_failed": "Refusé par votre banque",
    "gw.state.failed": "Échoué",
    "gw.state.rejected": "Refusé",
    "gw.state.abandoned": "Abandonné",
    "gw.state.unknown": "Statut inconnu",
  },
  en: {
    "gw.wallet.title": "Wallet",
    "gw.wallet.loading": "Loading balance…",
    "gw.wallet.loadError": "Could not load your balance right now.",
    "gw.wallet.retry": "Retry",

    "gw.wallet.frozenBanner": "Your payouts are frozen. Contact Solunary to find out why.",
    "gw.wallet.debtBanner": "You owe Solunary {amount} — this will be deducted from your next payouts.",
    "gw.wallet.commission": "Solunary commission on sales settled through the gateway: {pct}%.",
    "gw.wallet.adjustments": "Adjustments to date: {amount}",

    "gw.wallet.bucket.pending": "Pending clearance",
    "gw.wallet.bucket.available": "Available",
    "gw.wallet.bucket.reserved": "Reserved",
    "gw.wallet.bucket.lifetimePaid": "Total paid out to date",

    "gw.wallet.holdsTitle": "Release schedule",
    "gw.wallet.holdReleasesAt": "Releases {date}",

    "gw.wallet.withdrawAmountLabel": "Amount to withdraw",
    "gw.wallet.requestWithdrawal": "Request withdrawal",
    "gw.wallet.frozenBlocksWithdrawal": "Withdrawals are blocked while your account is frozen.",
    "gw.wallet.destinationNotReady": "You cannot withdraw until your bank payout destination is connected.",
    "gw.wallet.destinationSetupCta": "Set up my payout destination",
    "gw.wallet.belowMinimum": "The minimum withdrawal is {min}.",
    "gw.wallet.aboveAvailable": "You cannot withdraw more than what's available ({available}).",
    "gw.wallet.expectsReview": "Requests above {max} are reviewed by a person — expect a delay.",

    "gw.wallet.confirmTitle": "Confirm withdrawal",
    "gw.wallet.confirmDesc": "This amount will be sent once Solunary approves it.",
    "gw.wallet.receiveNow": "You receive {amount} now.",
    "gw.wallet.reserveHeld": "{amount} ({pct}%) is held as a rolling reserve until {date}.",
    "gw.wallet.crossBorderNote": "Your bank account is in another country: Stripe charges an international transfer fee, deducted from your balance.",
    "gw.wallet.conversionEstimate": "Stripe converts this payout to {currency}. Estimated at the rate your recent sales settled at: {gross}, less a conversion fee of about {fee} ({pct}%), so about {net} arrives. Stripe sets the actual rate at payout.",
    "gw.wallet.conversionNoHistory": "Stripe converts this payout to {currency} and charges a conversion fee of about {pct}%. No {currency} estimate can be shown until one of your sales in {currency} has settled.",
    "gw.wallet.cancel": "Cancel",
    "gw.wallet.confirmSend": "Confirm",
    "gw.wallet.withdrawalApproved": "Withdrawal auto-approved.",
    "gw.wallet.withdrawalRequested": "Withdrawal request sent.",

    "gw.wallet.historyTitle": "Withdrawal history",
    "gw.wallet.historyEmpty": "No withdrawals yet.",
    "gw.wallet.colAmount": "Amount",
    "gw.wallet.colStatus": "Status",
    "gw.wallet.colRequested": "Requested",
    "gw.wallet.colFailure": "Failure reason",
    "gw.wallet.bankLegFailedHint": "The money is still on your Stripe balance. Update your bank details in Stripe to receive it.",

    "gw.state.reserving": "Reserving",
    "gw.state.requested": "Requested",
    "gw.state.approved": "Approved",
    "gw.state.transfer_created": "Transfer started",
    "gw.state.transfer_unknown": "Verifying with the bank",
    "gw.state.payout_pending": "Transfer in progress",
    "gw.state.paid": "Paid out",
    "gw.state.payout_leg_failed": "Refused by your bank",
    "gw.state.failed": "Failed",
    "gw.state.rejected": "Rejected",
    "gw.state.abandoned": "Abandoned",
    "gw.state.unknown": "Unknown status",
  },
  es: {
    "gw.wallet.title": "Billetera",
    "gw.wallet.loading": "Cargando saldo…",
    "gw.wallet.loadError": "No se pudo cargar el saldo en este momento.",
    "gw.wallet.retry": "Reintentar",

    "gw.wallet.frozenBanner": "Sus reversos están congelados. Contacte a Solunary para saber el motivo.",
    "gw.wallet.debtBanner": "Usted debe {amount} a Solunary — se deducirá de sus próximos reversos.",
    "gw.wallet.commission": "Comisión de Solunary sobre las ventas cobradas por la pasarela: {pct} %.",
    "gw.wallet.adjustments": "Ajustes acumulados: {amount}",

    "gw.wallet.bucket.pending": "En proceso de cobro",
    "gw.wallet.bucket.available": "Disponible",
    "gw.wallet.bucket.reserved": "Reservado",
    "gw.wallet.bucket.lifetimePaid": "Total reversado hasta la fecha",

    "gw.wallet.holdsTitle": "Calendario de liberación",
    "gw.wallet.holdReleasesAt": "Se libera el {date}",

    "gw.wallet.withdrawAmountLabel": "Monto a retirar",
    "gw.wallet.requestWithdrawal": "Solicitar retiro",
    "gw.wallet.frozenBlocksWithdrawal": "Los retiros están bloqueados mientras su cuenta esté congelada.",
    "gw.wallet.destinationNotReady": "No puede retirar hasta que su cuenta bancaria de pago esté conectada.",
    "gw.wallet.destinationSetupCta": "Configurar mi cuenta de pago",
    "gw.wallet.belowMinimum": "El retiro mínimo es de {min}.",
    "gw.wallet.aboveAvailable": "No puede retirar más de lo disponible ({available}).",
    "gw.wallet.expectsReview": "Las solicitudes superiores a {max} son revisadas por una persona — espere una demora.",

    "gw.wallet.confirmTitle": "Confirmar retiro",
    "gw.wallet.confirmDesc": "Este monto se enviará en cuanto Solunary lo apruebe.",
    "gw.wallet.receiveNow": "Recibe {amount} ahora.",
    "gw.wallet.reserveHeld": "{amount} ({pct} %) se retiene como reserva renovable hasta el {date}.",
    "gw.wallet.crossBorderNote": "Su cuenta bancaria está en otro país: Stripe cobra una comisión por transferencia internacional, que se deduce de su saldo.",
    "gw.wallet.conversionEstimate": "Stripe convierte este pago a {currency}. Estimación al tipo al que se liquidaron sus ventas recientes: {gross}, menos una comisión de conversión de aproximadamente {fee} ({pct} %), es decir, unos {net} recibidos. Stripe fija el tipo real en el momento del pago.",
    "gw.wallet.conversionNoHistory": "Stripe convierte este pago a {currency} y cobra una comisión de conversión de aproximadamente {pct} %. No se puede mostrar ninguna estimación en {currency} hasta que se haya liquidado alguna de sus ventas en {currency}.",
    "gw.wallet.cancel": "Cancelar",
    "gw.wallet.confirmSend": "Confirmar",
    "gw.wallet.withdrawalApproved": "Retiro aprobado automáticamente.",
    "gw.wallet.withdrawalRequested": "Solicitud de retiro enviada.",

    "gw.wallet.historyTitle": "Historial de retiros",
    "gw.wallet.historyEmpty": "Aún no hay retiros.",
    "gw.wallet.colAmount": "Monto",
    "gw.wallet.colStatus": "Estado",
    "gw.wallet.colRequested": "Solicitado el",
    "gw.wallet.colFailure": "Motivo del fallo",
    "gw.wallet.bankLegFailedHint": "El dinero sigue en su saldo de Stripe. Actualice sus datos bancarios en Stripe para recibirlo.",

    "gw.state.reserving": "Reservando",
    "gw.state.requested": "Solicitado",
    "gw.state.approved": "Aprobado",
    "gw.state.transfer_created": "Transferencia iniciada",
    "gw.state.transfer_unknown": "Verificando con el banco",
    "gw.state.payout_pending": "Transferencia en curso",
    "gw.state.paid": "Reversado",
    "gw.state.payout_leg_failed": "Rechazado por su banco",
    "gw.state.failed": "Fallido",
    "gw.state.rejected": "Rechazado",
    "gw.state.abandoned": "Abandonado",
    "gw.state.unknown": "Estado desconocido",
  },
};
